import type { ReactNode } from 'react';

import { useCallback, useEffect, useRef } from 'react';
import FLAGS from '../../constants/flags';
import logHandledError from '@blueshift-ui/telemetry/dist/helpers/log-handled-error';
import useAuthenticatedUser from '@blueshift-ui/auth/dist/hooks/use-authenticated-user';
import useFeatureFlag from '@blueshift-ui/auth/dist/hooks/use-feature-flag';
import useIsVt4sStudent from '../../hooks/use-is-vt4s-student';

declare global {
  interface Window {
    zE?: any;
  }
}

const zendeskSnippetURL = 'https://static.zdassets.com/ekr/snippet.js';

type ZendeskChatWidget = {
  children: ReactNode;
};

const ZendeskChatWidget = ({ children }: ZendeskChatWidget) => {
  const { hasAccess: isVt4sStudent } = useIsVt4sStudent();
  const { value: chatWidgetIsEnabled } = useFeatureFlag(FLAGS.chatWidget);
  const { value: zendeskAuthenticationEnabled } = useFeatureFlag(
    FLAGS.zendeskAuthenticatedMessaging
  );
  const { value: zendeskChannelKey, isLoading: isLoadingZendeskChannelKey } = useFeatureFlag(
    FLAGS.zendeskChannelKey
  );
  const zendeskScriptURL = `${zendeskSnippetURL}?key=${zendeskChannelKey}`;
  const { data: user, isInitialLoading: isLoadingAuthenticatedUser } = useAuthenticatedUser({
    enabled: Boolean(zendeskAuthenticationEnabled),
  });
  const hideChatWidget = isVt4sStudent || !chatWidgetIsEnabled || isLoadingZendeskChannelKey;
  const scriptId = 'ze-snippet';
  // Track if the component added the script to avoid removing it on unmount if not added by this component
  const scriptAddedByComponent = useRef(false);

  const existingScript = useCallback(() => {
    return Array.from(document.getElementsByTagName('script')).find(
      (script) => script.src === zendeskScriptURL
    );
  }, [zendeskScriptURL]);

  const loadZendeskScript = useCallback(() => {
    const script = document.createElement('script');
    script.src = zendeskScriptURL;
    script.id = scriptId;
    script.defer = true;
    script.setAttribute('data-testid', scriptId);
    document.body.appendChild(script);
    script.onload = () => {
      if (window.zE) {
        window.zE('messenger:set', 'conversationTags', ['mylearn-account']);

        if (zendeskAuthenticationEnabled) {
          window.zE('messenger', 'loginUser', (callback: (jwt: string) => void) => {
            if (user?.zendesk_jwt) {
              callback(user.zendesk_jwt);
            } else {
              logHandledError('zendesk_jwt not fetched', {
                userId: user?.id,
                clientId: user?.client_id,
              });
            }
          });
        }
      }

      /**
       * Check if zendesk is loaded correctly.
       * Unfortunately we have no control of when and how zendesk loads.
       * The best we can do is to wait a reasonable amount of seconds before expecting the zendesk widget to be injected.
       */
      setTimeout(() => {
        if (!document.querySelector('iframe#launcher')) {
          logHandledError(`[Zendesk Error]: Failed to load script from ${zendeskScriptURL}`, {
            zendeskScriptURL,
          });
        }
      }, 3000);
    };
    scriptAddedByComponent.current = true;
  }, [
    user?.client_id,
    user?.id,
    user?.zendesk_jwt,
    zendeskAuthenticationEnabled,
    zendeskScriptURL,
  ]);

  useEffect(() => {
    if (
      hideChatWidget ||
      existingScript() ||
      (zendeskAuthenticationEnabled && isLoadingAuthenticatedUser)
    ) {
      return;
    }

    loadZendeskScript();

    return () => {
      if (scriptAddedByComponent.current) {
        const existingZendeskScript = existingScript();

        if (existingZendeskScript) {
          existingZendeskScript.remove();
        }
      }
    };
  }, [
    hideChatWidget,
    isLoadingAuthenticatedUser,
    loadZendeskScript,
    zendeskAuthenticationEnabled,
    zendeskScriptURL,
    existingScript,
  ]);

  return <>{children}</>;
};

export default ZendeskChatWidget;
export { zendeskSnippetURL };
